import { defineStore } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import axios from 'axios'

export const useModuleConnectionStore = defineStore('moduleConnection', {
  state: () => ({
    // Кэш связанных модулей: { moduleId: { type: moduleData } }
    connections: {},
    // Статус загрузки: { moduleId: 'loading' | 'loaded' | 'error' }
    loadingStatus: {}
  }),

  getters: {
    /**
     * Получить все связи для модуля
     */
    getConnections: (state) => (moduleId) => {
      return state.connections[moduleId] || {}
    },

    /**
     * Получить конкретное подключение по типу
     */
    getConnection: (state) => (moduleId, connectionType) => {
      return state.connections[moduleId]?.[connectionType] || null
    },

    /**
     * Проверить есть ли подключение
     */
    hasConnection: (state) => (moduleId, connectionType) => {
      return !!state.connections[moduleId]?.[connectionType]
    },

    /**
     * Проверить есть ли доступ у пользователя к подключенному модулю
     */
    hasAccessToConnection: (state) => (moduleId, connectionType) => {
      const connection = state.connections[moduleId]?.[connectionType]
      if (!connection) return false

      const authStore = useAuthStore()
      
      // Супер админ имеет доступ ко всему
      if (authStore.isSuperAdmin) return true

      // Проверяем есть ли у пользователя доступ к связанному модулю
      return authStore.userModules?.some(
        userModule => userModule.id === connection.id
      )
    },

    /**
     * Получить статус подключения с информацией о доступе
     */
    getConnectionStatus: (state) => (moduleId, connectionType) => {
      const authStore = useAuthStore()
      const connection = state.connections[moduleId]?.[connectionType]

      // Человеческие названия модулей для сообщений
      const moduleNames = {
        'storehouse': 'складского учета',
        'picking-desk': 'комплектования',
        'tsd-scan': 'ТСД сканирования',
        'production': 'производства',
        'orders': 'заказов'
      }

      const moduleName = moduleNames[connectionType] || connectionType

      if (!connection) {
        return {
          exists: false,
          hasAccess: false,
          connection: null,
          message: `Модуль ${moduleName} не подключен к рабочему месту`
        }
      }

      const hasAccess = authStore.isSuperAdmin || authStore.isAdmin || authStore.userModules?.some(
        userModule => userModule.id === connection.id
      )

      return {
        exists: true,
        hasAccess,
        connection,
        message: hasAccess 
          ? 'Доступ разрешен' 
          : `У вас нет доступа к модулю ${moduleName}. Обратитесь к администратору.`
      }
    },

    /**
     * Проверить загружены ли связи для модуля
     */
    isLoaded: (state) => (moduleId) => {
      return state.loadingStatus[moduleId] === 'loaded'
    },

    /**
     * Проверить загружаются ли связи
     */
    isLoading: (state) => (moduleId) => {
      return state.loadingStatus[moduleId] === 'loading'
    }
  },

  actions: {
    /**
     * Загрузить связанные модули из API
     */
    async loadConnections(moduleId) {
      // Если уже загружены - пропускаем
      if (this.loadingStatus[moduleId] === 'loaded') {
        return
      }

      // Если уже загружаются - ждем
      if (this.loadingStatus[moduleId] === 'loading') {
        return new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            if (this.loadingStatus[moduleId] !== 'loading') {
              clearInterval(checkInterval)
              resolve()
            }
          }, 100)
        })
      }

      this.loadingStatus[moduleId] = 'loading'

      try {
        const response = await axios.get(`/api/module-connection-storehouse/${moduleId}/connections`)

        if (response.data.success) {
          const connectedModules = response.data.data

          // Формируем объект связей по типам
          const connections = {}
          
          connectedModules.forEach(module => {
            if (module.type && module.type.key) {
              connections[module.type.key] = {
                id: module.id,
                name: module.name,
                type: module.type
              }
            }
          })

          // Сохраняем в state
          this.connections[moduleId] = connections
          this.loadingStatus[moduleId] = 'loaded'
        } else {
          this.connections[moduleId] = {}
          this.loadingStatus[moduleId] = 'error'
        }
      } catch (error) {
        console.error(`Ошибка загрузки связей для модуля ${moduleId}:`, error)
        this.connections[moduleId] = {}
        this.loadingStatus[moduleId] = 'error'
      }
    },

    /**
     * Очистить кэш для модуля
     */
    clearConnections(moduleId) {
      delete this.connections[moduleId]
      delete this.loadingStatus[moduleId]
    },

    /**
     * Очистить весь кэш
     */
    clearAll() {
      this.connections = {}
      this.loadingStatus = {}
    }
  }
})

